import { Injectable, NgZone, signal } from "@angular/core";
import { Subscription } from "rxjs";
import { AuthService } from "./auth.service";
import { DriverService } from "./driver.service";
import { ProfileEnum } from "@/providers/enums/profile-Enum";

@Injectable({
    providedIn: "root",
})
export class DriverPositionService {
    private static readonly INTERVAL_MS = 20 * 1000;

    private intervalId: ReturnType<typeof setInterval> | null = null;
    private watchId: number | null = null;
    private authSubscription: Subscription | null = null;
    private lastPosition: GeolocationCoordinates | null = null;
    private permissionStatus: PermissionStatus | null = null;
    private sending = false;

    readonly locationDenied = signal(false);

    constructor(
        private readonly ngZone: NgZone,
        private readonly authService: AuthService,
        private readonly driverService: DriverService
    ) { }

    isDriver(): boolean {
        const perfil = this.authService.perfil();
        return perfil == ProfileEnum.Motorista || perfil == ProfileEnum.Entregador;
    }

    start(): void {
        if (this.watchId !== null || !this.isDriver() || !this.authService.isAuthenticated()) return;

        if (!("geolocation" in navigator)) {
            console.error("Geolocalização indisponível no dispositivo.");
            return;
        }

        this.authSubscription = this.authService.isAuthenticated$.subscribe((authenticated) => {
            if (!authenticated) this.stop();
        });

        this.ngZone.runOutsideAngular(() => {
            // watchPosition mantém o GPS ativo e entrega leituras sem estourar timeout
            this.watchId = navigator.geolocation.watchPosition(
                (pos) => {
                    this.locationDenied.set(false);
                    const isFirstPosition = this.lastPosition === null;
                    this.lastPosition = pos.coords;
                    if (isFirstPosition) this.sendPosition();
                },
                (err) => this.handleError(err),
                { enableHighAccuracy: true, maximumAge: DriverPositionService.INTERVAL_MS }
            );

            this.intervalId = setInterval(() => this.sendPosition(), DriverPositionService.INTERVAL_MS);
        });
    }

    stop(): void {
        if (this.watchId !== null) {
            navigator.geolocation.clearWatch(this.watchId);
            this.watchId = null;
        }

        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        if (this.permissionStatus) {
            this.permissionStatus.onchange = null;
            this.permissionStatus = null;
        }

        this.authSubscription?.unsubscribe();
        this.authSubscription = null;
        this.lastPosition = null;
        this.sending = false;
        this.locationDenied.set(false);
    }

    retry(): void {
        this.stop();
        this.start();
    }

    private handleError(err: GeolocationPositionError): void {
        if (err.code === err.PERMISSION_DENIED) {
            console.error("Permissão de localização negada. Habilite a localização do navegador para este site.", err.message);
            this.stop();
            this.locationDenied.set(true);
            this.waitForPermission();
            return;
        }

        console.warn("Erro ao ler GPS do motorista:", err.message);
    }

    // Retoma o envio automaticamente quando o motorista liberar a localização nas configurações
    private async waitForPermission(): Promise<void> {
        if (!navigator.permissions?.query) return;

        try {
            const status = await navigator.permissions.query({ name: "geolocation" });
            if (!this.authService.isAuthenticated()) return;

            this.permissionStatus = status;
            status.onchange = () => {
                if (status.state !== "denied") {
                    status.onchange = null;
                    this.permissionStatus = null;
                    this.start();
                }
            };
        } catch {
            // Navegador sem suporte a Permissions API para geolocalização
        }
    }

    private async sendPosition(): Promise<void> {
        if (this.sending || !this.lastPosition) return;

        if (!this.authService.isAuthenticated()) {
            this.stop();
            return;
        }

        this.sending = true;
        try {
            await this.driverService.getPosicaoMorotista(
                {
                    ultimaLatitude: this.truncateCoordinate(this.lastPosition.latitude),
                    ultimaLongitude: this.truncateCoordinate(this.lastPosition.longitude)
                },
                this.authService.userId()
            );
        } catch (e) {
            console.warn("Falha ao atualizar posição do motorista:", e);
        } finally {
            this.sending = false;
        }
    }

    // Mantém 7 casas decimais sem arredondar (-7.186463789304967 => -7.1864637)
    private truncateCoordinate(value: number): number {
        const [integer, decimals = ""] = value.toString().split(".");
        return Number(`${integer}.${decimals.substring(0, 7)}`);
    }
}
